import type { Cell } from 'write-excel-file/browser';

const XLSX_MIME_TYPE =
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';

/** The short date format SheetJS gave a `Date` cell; kept so a date reads as it did. */
const DATE_FORMAT = 'm/d/yy';
const DAY_MS = 24 * 60 * 60 * 1000;
const EXCEL_EPOCH = Date.UTC(1899, 11, 30);

export interface XlsxSheet {
    /** Already a valid, unique Excel sheet name (see `uniqueExcelSheetNames`). */
    name: string;
    rows: readonly (readonly unknown[])[];
}

/**
 * The Excel serial number of the wall-clock time `date` has in this browser, which is how
 * SheetJS stored dates. write-excel-file would count from UTC instead.
 */
export function excelSerialDate(date: Date): number {
    const wallClock = Date.UTC(
        date.getFullYear(),
        date.getMonth(),
        date.getDate(),
        date.getHours(),
        date.getMinutes(),
        date.getSeconds(),
        date.getMilliseconds()
    );
    return (wallClock - EXCEL_EPOCH) / DAY_MS;
}

function toCell(value: unknown): Cell {
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
        return value;
    }
    if (value instanceof Date) {
        return { value: excelSerialDate(value), type: Number, format: DATE_FORMAT };
    }
    // Missing values and anything that is not a plain value leave the cell empty, as before.
    return null;
}

/** An .xlsx workbook with one sheet per entry, in order, each row written from A. */
export async function xlsxBlob(sheets: readonly XlsxSheet[]): Promise<Blob> {
    const { default: writeXlsxFile } = await import('write-excel-file/browser');
    const workbook = await writeXlsxFile(
        sheets.map((sheet) => ({
            sheet: sheet.name,
            data: sheet.rows.map((row) => row.map(toCell)),
        }))
    ).toBlob();
    return new Blob([workbook], { type: XLSX_MIME_TYPE });
}
